import { apiClient } from '../api/client';

export interface WikiCategory {
  id: string;
  storeId: string;
  name: string;
  slug: string;
  description?: string;
  iconName?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WikiPage {
  id: string;
  storeId: string;
  categoryId: string;
  categoryName?: string;
  categorySlug?: string;
  title: string;
  slug: string;
  content?: string;
  excerpt?: string;
  order: number;
  isActive?: boolean;
  isPublished?: boolean;
  isFeatured?: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
  attachments?: WikiAttachment[];
  tags?: WikiTag[];
}

export interface WikiAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  createdAt: string;
}

export interface WikiTag {
  id: string;
  name: string;
  slug: string;
  color?: string | null;
  count?: number;
}

export interface WikiFeedback {
  isHelpful: boolean;
  comment?: string;
}

export const wikiService = {
  /**
   * Busca categorias da wiki (público)
   */
  async getCategories(storeId: string): Promise<WikiCategory[]> {
    const response = await apiClient.get<{ categories: WikiCategory[] }>(
      `/public/stores/${storeId}/wiki/categories`
    );
    return response.categories;
  },

  /**
   * Busca páginas da wiki (público)
   */
  async getPages(storeId: string, categoryId?: string, tagIds?: string[]): Promise<WikiPage[]> {
    let params = '';
    if (categoryId) {
      params += `?categoryId=${categoryId}`;
    }
    if (tagIds && tagIds.length > 0) {
      params += params ? '&' : '?';
      params += `tagIds=${tagIds.join(',')}`;
    }
    const response = await apiClient.get<{ pages: WikiPage[] }>(
      `/public/stores/${storeId}/wiki/pages${params}`
    );
    return response.pages;
  },

  /**
   * Busca tags mais populares (público)
   */
  async getPopularTags(storeId: string): Promise<WikiTag[]> {
    const response = await apiClient.get<{ tags: WikiTag[] }>(
      `/public/stores/${storeId}/wiki/tags/popular`
    );
    return response.tags;
  },

  /**
   * Busca páginas mais visualizadas (público)
   */
  async getMostViewedPages(storeId: string, limit: number = 5): Promise<WikiPage[]> {
    const response = await apiClient.get<{ pages: WikiPage[] }>(
      `/public/stores/${storeId}/wiki/most-viewed?limit=${limit}`
    );
    return response.pages;
  },

  /**
   * Busca uma página específica por slug (público)
   */
  async getPageBySlug(storeId: string, slug: string): Promise<WikiPage> {
    const response = await apiClient.get<{ page: WikiPage }>(
      `/public/stores/${storeId}/wiki/pages/${slug}`
    );
    return response.page;
  },

  /**
   * Envia feedback de uma página (público)
   */
  async submitFeedback(
    storeId: string,
    pageId: string,
    feedback: WikiFeedback
  ): Promise<void> {
    await apiClient.post(
      `/public/stores/${storeId}/wiki/pages/${pageId}/feedback`,
      feedback
    );
  },
};
